import React, { useState, useEffect } from 'react';
import { Search, Music } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { musikkAPI } from '@/services/api/musikk.api';
import type { Musikk, MusikkTransponering } from '@/services/api/types';

interface Props {
  open: boolean;
  onClose: () => void;
  onSelectSong: (song: Musikk, transponering: MusikkTransponering | null) => void;
}

/**
 * SongSearchDialog - Dialog for å søke og velge sang fra musikkbibliotek
 * Viser søkeresultater, sanginfo, og lar bruker velge transponering
 */
const SongSearchDialog: React.FC<Props> = ({ open, onClose, onSelectSong }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [songs, setSongs] = useState<Musikk[]>([]);
  const [selectedSong, setSelectedSong] = useState<Musikk | null>(null);
  const [transponeringer, setTransponeringer] = useState<MusikkTransponering[]>([]);
  const [selectedTransponeringId, setSelectedTransponeringId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);

  // Reset state når dialog åpnes/lukkes
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setSongs([]);
      setSelectedSong(null);
      setTransponeringer([]);
      setSelectedTransponeringId('');
    }
  }, [open]);

  // Søk etter sanger
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSongs([]);
      return;
    }

    const searchSongs = async () => {
      setSearching(true);
      try {
        const { musikk } = await musikkAPI.getAll({ sok: searchTerm, publisert: true });
        setSongs(musikk);
      } catch (error) {
        console.error('Feil ved søk:', error);
        setSongs([]);
      } finally {
        setSearching(false);
      }
    };

    // Debounce søket
    const debounceTimer = setTimeout(searchSongs, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Last transponeringer når sang velges
  useEffect(() => {
    if (!selectedSong) {
      setTransponeringer([]);
      setSelectedTransponeringId('');
      return;
    }

    const fetchTransponeringer = async () => {
      setLoading(true);
      try {
        const { transponeringer } = await musikkAPI.getAllTransponeringer(selectedSong.id);
        setTransponeringer(transponeringer);
        // Sett default til "original"
        setSelectedTransponeringId('original');
      } catch (error) {
        console.error('Feil ved henting av transponeringer:', error);
        setTransponeringer([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTransponeringer();
  }, [selectedSong]);

  const handleSelectSong = (song: Musikk) => {
    setSelectedSong(song);
  };

  const handleConfirm = () => {
    if (!selectedSong) return;

    const transponering = (selectedTransponeringId && selectedTransponeringId !== 'original')
      ? transponeringer.find(t => t.id.toString() === selectedTransponeringId) || null
      : null;

    onSelectSong(selectedSong, transponering);
    onClose();
  };

  const formatDuration = (song: Musikk) => {
    if (!song.lengde_minutter) return '-';
    const min = song.lengde_minutter;
    const sek = song.lengde_sekunder || 0;
    return `${min}:${sek.toString().padStart(2, '0')}`;
  };

  const getToneartDisplay = (song: Musikk) => {
    if (!song.grunntone || !song.toneart_type) return '-';
    return `${song.grunntone} ${song.toneart_type}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Music className="h-5 w-5 text-primary" />
            Legg til sang
          </DialogTitle>
          <DialogDescription>
            Søk etter sang i musikkbiblioteket og velg toneart
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Søkefelt */}
          <div className="space-y-2">
            <Label htmlFor="search">Søk etter sang</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                placeholder="Søk på tittel, artist eller album..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
          </div>

          {/* Søkeresultater - Tabell */}
          {searchTerm && (
            <div className="space-y-2">
              {searching && (
                <div className="text-sm text-muted-foreground text-center py-8 border rounded-lg bg-muted/20">
                  Søker...
                </div>
              )}

              {!searching && songs.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8 border rounded-lg bg-muted/20">
                  Ingen sanger funnet
                </div>
              )}

              {!searching && songs.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[400px] overflow-y-auto">
                    <Table>
                      <TableHeader className="bg-muted/50 sticky top-0 z-10">
                        <TableRow>
                          <TableHead className="w-[40%]">Tittel</TableHead>
                          <TableHead className="w-[30%]">Artist</TableHead>
                          <TableHead className="w-[15%]">Toneart</TableHead>
                          <TableHead className="w-[15%]">Varighet</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {songs.map((song) => (
                          <TableRow
                            key={song.id}
                            onClick={() => handleSelectSong(song)}
                            className={`cursor-pointer transition-colors ${
                              selectedSong?.id === song.id
                                ? 'bg-primary/10 hover:bg-primary/15'
                                : 'hover:bg-muted/50'
                            }`}
                          >
                            <TableCell className="font-medium">
                              {song.tittel}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {song.artist || '-'}
                            </TableCell>
                            <TableCell className="text-sm">
                              {getToneartDisplay(song)}
                            </TableCell>
                            <TableCell className="text-sm">
                              {formatDuration(song)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Toneart-selector - Grået ut til sang er valgt */}
          <div className="space-y-2 border-t pt-4">
            <Label 
              htmlFor="transponering" 
              className={!selectedSong ? 'text-muted-foreground' : ''}
            >
              Toneart (valgfri)
            </Label>
            
            {!selectedSong && (
              <div className="p-3 border rounded-lg bg-muted/30 text-sm text-muted-foreground">
                Velg en sang fra tabellen over for å velge toneart
              </div>
            )}

            {selectedSong && loading && (
              <div className="p-3 border rounded-lg bg-muted/30 text-sm text-muted-foreground">
                Laster transponeringer...
              </div>
            )}

            {selectedSong && !loading && transponeringer.length === 0 && (
              <div className="p-3 border rounded-lg bg-muted/30 text-sm text-muted-foreground">
                Ingen transponeringer tilgjengelig. Original toneart ({getToneartDisplay(selectedSong)}) vil brukes.
              </div>
            )}

            {selectedSong && !loading && transponeringer.length > 0 && (
              <>
                <Select 
                  value={selectedTransponeringId} 
                  onValueChange={setSelectedTransponeringId}
                >
                  <SelectTrigger id="transponering">
                    <SelectValue placeholder="Velg toneart" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="original">
                      Original ({getToneartDisplay(selectedSong)})
                    </SelectItem>
                    {transponeringer.map((t) => (
                      <SelectItem key={t.id} value={t.id.toString()}>
                        {t.navn} ({t.grunntone} {t.toneart_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Velg toneart for sangen
                </p>
              </>
            )}
          </div>
        </div>

        {/* Footer buttons */}
        <div className="flex justify-end gap-2 sm:gap-3 border-t pt-4 mt-4">
          <Button variant="outline" onClick={onClose}>
            Avbryt
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedSong}>
            Legg til sang
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SongSearchDialog;
